/**
 * AdminEnrollment — post-approval enrollment-change review queue.
 *
 * Fires when a student's enrollment status changes AFTER their campaign has
 * gone live. Per policy, campaigns are NEVER auto-suspended. Each item
 * requires an explicit admin decision.
 *
 * Two severity classes:
 *
 *   compassionate           financial hold / non-payment. Warm treatment.
 *                           Default lean = keep campaign live, offer support.
 *   recommended-suspension  academic-standing failure or withdrawal.
 *                           Harder treatment. Default lean = suspend,
 *                           unless admin sees mitigating context.
 *
 * Both severities REQUIRE the admin to pick one of two decisions:
 *   Keep live · Suspend campaign
 * There is no silent auto-resolution.
 *
 * TODO(backend): swap in-memory mock for:
 *   GET  /api/admin/enrollment-changes
 *   POST /api/admin/enrollment-changes/:id/{keep|suspend} { note }
 */

import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  ShieldAlert, Heart, PauseOctagon, Check, ExternalLink, RefreshCw,
} from "lucide-react";
import { SectionHeader, AdminButton, EmptyState, StatusBadge } from "./_ui";
import { ENROLLMENT_CHANGES } from "../../data/adminMock";

const fmt$ = (n) => `$${Number(n).toLocaleString()}`;

const REASON_META = {
  "non-payment": {
    label: "Financial hold / non-payment",
    desc:  "Not always about the student. Try to keep the campaign live and reach out.",
  },
  "academic-standing": {
    label: "Academic standing",
    desc:  "GPA / probation issue. Weigh the ask against the student's current standing.",
  },
  "withdrawn": {
    label: "Withdrew from term",
    desc:  "No longer enrolled. Confirm reason before deciding.",
  },
  "other": {
    label: "Other change",
    desc:  "Reason unspecified by the source system.",
  },
};

const SEVERITY_META = {
  "compassionate": {
    tone: "tangerine",
    label: "Compassionate review",
    icon: Heart,
    stripe: "bg-tangerine",
    badge: "bg-tangerine/15 text-tangerine ring-tangerine/30",
    card:  "border-tangerine/30 bg-tangerine/[0.04]",
    header:"border-tangerine/20 bg-tangerine/[0.06]",
    hint:  "Default lean: keep the campaign live.",
  },
  "recommended-suspension": {
    tone: "scarlet",
    label: "Academic suspension recommendation",
    icon: PauseOctagon,
    stripe: "bg-scarlet",
    badge: "bg-scarlet/15 text-scarlet ring-scarlet/30",
    card:  "border-scarlet/30 bg-scarlet/[0.04]",
    header:"border-scarlet/20 bg-scarlet/[0.06]",
    hint:  "Default lean: suspend the campaign unless mitigating context.",
  },
};

export const AdminEnrollment = () => {
  const [queue, setQueue] = useState(ENROLLMENT_CHANGES);
  const [notes, setNotes] = useState({}); // keyed by change id

  const pending  = queue.filter((c) => c.reviewStatus === "pending");
  const resolved = queue.filter((c) => c.reviewStatus !== "pending");

  const resolve = (id, action /* "keep" | "suspend" */) => {
    setQueue((prev) => prev.map((c) => c.id === id
      ? { ...c, reviewStatus: action === "keep" ? "resolved-keep" : "resolved-suspend",
          reviewedAt: new Date().toISOString(), reviewNote: notes[id] || "" }
      : c
    ));
    toast[action === "keep" ? "success" : "message"](
      action === "keep"
        ? "Campaign kept live"
        : "Campaign suspended",
      {
        description: action === "keep"
          ? "Student will be emailed a compassionate check-in note."
          : "The student will be notified. Funds already raised are held pending review.",
      },
    );
  };

  return (
    <div className="space-y-10">
      <SectionHeader
        eyebrow="Enrollment changes"
        title="Compassionate reviews and academic suspension recommendations."
        description="When a live student's enrollment status changes, the case appears here. Two tracks: compassionate (financial holds, warm treatment) and academic suspension recommendation (probation or withdrawal). Every case requires an explicit decision — never auto-suspended."
        action={
          <AdminButton variant="secondary" size="sm" testid="enroll-refresh">
            <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.75} /> Re-check NSC
          </AdminButton>
        }
      />

      {pending.length === 0 ? (
        <EmptyState
          title="Nothing to review"
          description="No enrollment changes flagged. When a live student's status changes, the case will appear here."
        />
      ) : (
        <ul className="space-y-5" data-testid="enrollment-pending-list">
          {pending.map((c) => {
            const sev = SEVERITY_META[c.severity] || SEVERITY_META.compassionate;
            const Icon = sev.icon;
            const reason = REASON_META[c.reason] || REASON_META.other;
            return (
              <li
                key={c.id}
                className={`overflow-hidden rounded-lg border bg-sand ${sev.card}`}
                data-testid={`enrollment-row-${c.id}`}
              >
                {/* Severity header strip */}
                <div className={`flex items-center justify-between gap-3 border-b px-5 py-3 ${sev.header}`}>
                  <div className="flex items-center gap-2">
                    <Icon className={`h-3.5 w-3.5 text-${sev.tone}`} strokeWidth={2} />
                    <span className={`text-[10px] font-semibold uppercase tracking-[0.2em] text-${sev.tone}`}>
                      {sev.label}
                    </span>
                    <span className="text-[11px] text-ink/60">· detected {c.detectedAt}</span>
                  </div>
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ring-1 ring-inset ${sev.badge}`}>
                    {reason.label}
                  </span>
                </div>

                {/* Body */}
                <div className="grid grid-cols-1 gap-5 px-5 py-5 lg:grid-cols-12">
                  {/* Left: student + reason */}
                  <div className="lg:col-span-7">
                    <h3 className="font-serif text-2xl leading-tight text-ink">{c.studentName}</h3>
                    <p className="mt-1 text-sm text-ink/70">{c.school}</p>

                    <div className="mt-4 rounded-md border border-ink/10 bg-beige/60 p-4">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/60">Reason</p>
                      <p className="mt-2 text-sm leading-relaxed text-ink">{c.reasonText}</p>
                      <p className="mt-2 text-xs italic text-ink/50">{sev.hint}</p>
                    </div>

                    <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
                      <p className="text-ink">
                        <span className="font-serif text-lg">{fmt$(c.raisedSoFar)}</span>{" "}
                        <span className="text-ink/50">of {fmt$(c.goal)} raised</span>
                      </p>
                      <Link
                        to={`/demo/campaign/${c.campaignId}`}
                        className="inline-flex items-center gap-1 text-xs font-medium text-ink hover:text-tangerine"
                        data-testid={`enrollment-view-${c.id}`}
                      >
                        View public campaign
                        <ExternalLink className="h-3 w-3" strokeWidth={2} />
                      </Link>
                    </div>
                  </div>

                  {/* Right: decision */}
                  <div className="lg:col-span-5">
                    <label className="block text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/60">
                      Decision note (optional)
                    </label>
                    <textarea
                      rows={3}
                      value={notes[c.id] || ""}
                      onChange={(e) => setNotes({ ...notes, [c.id]: e.target.value })}
                      placeholder="Notes captured for the audit trail…"
                      className="mt-2 w-full rounded-md border border-ink/15 bg-sand p-3 text-sm text-ink placeholder:text-ink/40 focus:outline-none focus:ring-2 focus:ring-ink/20"
                      data-testid={`enrollment-note-${c.id}`}
                    />
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <AdminButton
                        variant="approve"
                        size="sm"
                        onClick={() => resolve(c.id, "keep")}
                        testid={`keep-${c.id}`}
                      >
                        <Check className="h-3 w-3" strokeWidth={2} /> Keep campaign live
                      </AdminButton>
                      <AdminButton
                        variant="reject"
                        size="sm"
                        onClick={() => resolve(c.id, "suspend")}
                        testid={`suspend-${c.id}`}
                      >
                        <PauseOctagon className="h-3 w-3" strokeWidth={2} /> Suspend campaign
                      </AdminButton>
                    </div>
                    <p className="mt-2 text-[11px] italic text-ink/50">
                      Both decisions are logged with your name and timestamp. Student is emailed either way.
                    </p>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Resolved history */}
      {resolved.length > 0 && (
        <div>
          <h4 className="text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/60">
            Recent decisions ({resolved.length})
          </h4>
          <ul className="mt-3 divide-y divide-ink/10 overflow-hidden rounded-lg border border-ink/10 bg-sand">
            {resolved.map((c) => (
              <li key={c.id} className="flex items-center gap-4 px-5 py-3">
                <ShieldAlert className="h-3.5 w-3.5 shrink-0 text-ink/40" strokeWidth={1.75} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-ink">
                    <span className="font-medium">{c.studentName}</span>
                    <span className="text-ink/60"> · {c.school}</span>
                  </p>
                  <p className="mt-0.5 truncate text-xs text-ink/60">{c.reasonText}</p>
                </div>
                <StatusBadge status={c.reviewStatus === "resolved-keep" ? "approved" : "rejected"} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
